import {
  CalculationType,
  PriceUnitType,
  type RmCalculationType,
  type RrpCalculationType,
} from '@/api';
import { connectColors, themeColors } from '@/theme/colors';
import { Button, Tag } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import React, { useEffect, useMemo, useState } from 'react';
import type {
  PriceUnitCalculationSettings,
  PriceUnitSettingsTouchedField,
} from '../types';
import {
  comparePriceUnitIds,
  getPriceUnitSettingsValidation,
  isCalculationTypeLicensedProducts,
  isCalculationTypeTargetDrivenLpAndRrp,
  isRmRrpDisabled,
} from '../utils';
import { AdditionalOptionsSection } from './additional-options-section';
import { CalculationMethodsSection } from './calculation-methods-section';
import HorizontalLine from './horizontal-line';
import { SimulationSettingsSection } from './simulation-settings-section';

type PriceUnitSettingsCardProps = {
  priceUnitSettings: PriceUnitCalculationSettings;
  editableSettings: PriceUnitCalculationSettings[];
  settingsByPriceUnitId: Map<string, PriceUnitCalculationSettings>;
  canEdit?: boolean;
  isSelected?: boolean;
  isSaving?: boolean;
  mode?: 'edit' | 'create';
  onSelect?: (priceUnitId: string, checked: boolean) => void;
  onSave?: (priceUnitSettings: PriceUnitCalculationSettings) => void;
  onCancel?: () => void;
  calculationTypes: CalculationType[];
  rmCalculationTypes: RmCalculationType[];
  rrpCalculationTypes: RrpCalculationType[];
};

export const PriceUnitSettingsCard = ({
  priceUnitSettings: initialPriceUnitSettings,
  editableSettings,
  settingsByPriceUnitId,
  canEdit = true,
  isSelected = false,
  onSelect,
  onSave,
  onCancel,
  isSaving = false,
  mode = 'edit',
  calculationTypes,
  rmCalculationTypes,
  rrpCalculationTypes,
}: PriceUnitSettingsCardProps) => {
  const [priceUnitSettings, setPriceUnitSettings] =
    useState<PriceUnitCalculationSettings>(initialPriceUnitSettings);
  const [touchedFields, setTouchedFields] = useState<
    Set<PriceUnitSettingsTouchedField>
  >(new Set());

  useEffect(() => {
    setPriceUnitSettings(initialPriceUnitSettings);
    setTouchedFields(new Set<PriceUnitSettingsTouchedField>());
  }, [initialPriceUnitSettings]);

  const {
    priceUnitId,
    priceUnitName,
    calculationType,
    calculationBasisValue,
    autoCalcEnabled,
  } = priceUnitSettings;

  const isCalcTypeLp = isCalculationTypeLicensedProducts(calculationType);
  const isCalcTypeTargetDrivenLpAndRrp =
    isCalculationTypeTargetDrivenLpAndRrp(calculationType);
  const rmRrpDisabled = isRmRrpDisabled(calculationType);

  const calculationBasisOptions = useMemo(
    () =>
      editableSettings
        .filter(
          (s) =>
            s.year === priceUnitSettings.year && s.priceUnitId !== priceUnitId,
        )
        .map((s) => ({
          id: s.priceUnitId,
          label: `${s.priceUnitId} - ${s.priceUnitName}`,
        }))
        .sort((a, b) => comparePriceUnitIds(a.id, b.id)),
    [editableSettings, priceUnitSettings.year, priceUnitId],
  );

  const inheritedSimulationSettings = calculationBasisValue
    ? settingsByPriceUnitId.get(calculationBasisValue)
    : undefined;
  const isSimulationSettingsInherited = !!inheritedSimulationSettings;

  const simulationSettings = inheritedSimulationSettings ?? priceUnitSettings;
  const {
    isMissingCalculationType,
    isMissingRm,
    isMissingRrp,
    isMissingBreakEven,
    isMissingTargetRrp,
    isInvalidTargetLpIndex,
    isInvalidLpFmcTarget,
    isInvalidThreshold,
    isInvalidAboveThreshold,
    isInvalidBelowThreshold,
    hasValidationErrors,
  } = getPriceUnitSettingsValidation(
    priceUnitSettings,
    simulationSettings,
    isSimulationSettingsInherited,
  );

  const wasTouched = (field: PriceUnitSettingsTouchedField) =>
    touchedFields.has(field);

  const visibleValidation = {
    isMissingCalculationType:
      isMissingCalculationType && wasTouched('calculationType'),
    isMissingRm: isMissingRm && wasTouched('rmPercentage'),
    isMissingRrp: isMissingRrp && wasTouched('rrpType'),
    isMissingBreakEven: isMissingBreakEven && wasTouched('breakEvenFactor'),
    isMissingTargetRrp: isMissingTargetRrp && wasTouched('targetRrpIndex'),
    isInvalidTargetLpIndex:
      isInvalidTargetLpIndex && wasTouched('targetLpIndex'),
    isInvalidLpFmcTarget: isInvalidLpFmcTarget && wasTouched('lpFmcTarget'),
    isInvalidThreshold: isInvalidThreshold && wasTouched('threshold'),
    isInvalidAboveThreshold:
      isInvalidAboveThreshold &&
      (wasTouched('aboveThresholdMin') || wasTouched('aboveThresholdMax')),
    isInvalidBelowThreshold:
      isInvalidBelowThreshold &&
      (wasTouched('belowThresholdMin') || wasTouched('belowThresholdMax')),
  };

  const isEdited = React.useMemo(
    () =>
      JSON.stringify(priceUnitSettings) !==
      JSON.stringify(initialPriceUnitSettings),
    [priceUnitSettings, initialPriceUnitSettings],
  );

  const settingsToSave: PriceUnitCalculationSettings = {
    ...priceUnitSettings,
    targetLpIndex: simulationSettings.targetLpIndex,
    lpFmcTarget: simulationSettings.lpFmcTarget,
    threshold: simulationSettings.threshold,
    aboveThresholdMin: simulationSettings.aboveThresholdMin,
    aboveThresholdMax: simulationSettings.aboveThresholdMax,
    belowThresholdMin: simulationSettings.belowThresholdMin,
    belowThresholdMax: simulationSettings.belowThresholdMax,
  };

  const handleFieldChange = (
    field: string,
    value: string | boolean | undefined,
  ) => {
    if (!canEdit) return;
    setPriceUnitSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleFieldBlur = (field: PriceUnitSettingsTouchedField) => {
    if (!canEdit) return;
    setTouchedFields((prev) => new Set(prev).add(field));
  };

  const handleCalculationTypeChange = (value: string | undefined) => {
    if (!canEdit) return;
    handleFieldBlur('calculationType');
    const calcType = value as CalculationType | undefined;
    const isLicensed = calcType === CalculationType.LicensedProducts;
    const isNewCalcTypeTargetDrivenLpAndRrp =
      isCalculationTypeTargetDrivenLpAndRrp(calcType);
    setPriceUnitSettings((prev) => ({
      ...prev,
      calculationType: calcType,
      ...(isRmRrpDisabled(calcType) && {
        rmPercentage: undefined,
        rrpType: undefined,
      }),
      ...(isNewCalcTypeTargetDrivenLpAndRrp && { rmPercentage: undefined }),
      ...(!isLicensed && { breakEvenFactor: undefined }),
      ...(!isLicensed &&
        !isNewCalcTypeTargetDrivenLpAndRrp && { targetRrpIndex: undefined }),
    }));
  };

  const handleReset = () => {
    if (isCreateMode) {
      onCancel?.();
      return;
    }
    setPriceUnitSettings(initialPriceUnitSettings);
    setTouchedFields(new Set<PriceUnitSettingsTouchedField>());
  };

  const isCreateMode = mode === 'create';
  const isLead = priceUnitSettings.priceUnitType === PriceUnitType.Lead;

  return (
    <Flex
      borderRadius={8}
      border="2px solid"
      borderColor={
        isCreateMode
          ? themeColors.support.primary.muted
          : themeColors.surface.background.base.default
      }
      backgroundColor={
        isCreateMode
          ? themeColors.surface.background.base.muted
          : themeColors.surface.background.base.default
      }
      boxShadow={isCreateMode ? 'lg' : 'sm'}
      padding={8}
      flexDirection="column"
      alignItems="flex-start"
      gap={4}
      alignSelf="stretch"
      overflow="hidden"
      position="relative"
    >
      {/* Header */}
      <Flex alignItems="flex-start" gap={4} width="100%">
        <Flex flexDirection="column" gap={1}>
          <Flex alignItems="center" gap={2}>
            <Text className="body-sm-medium" color={connectColors.core.gray}>
              {priceUnitId}
            </Text>
          </Flex>
          <Text className="heading-md">
            {priceUnitName}
            {isCreateMode && <span>&nbsp; ({priceUnitSettings.year})</span>}
          </Text>
        </Flex>
        {isCreateMode && (
          <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
            <Tag size="small" variant="primary">
              NEW
            </Tag>
          </div>
        )}
      </Flex>

      {!isLead && (
        <>
          <HorizontalLine />
          <CalculationMethodsSection
            priceUnitSettings={priceUnitSettings}
            calculationTypes={calculationTypes}
            rmCalculationTypes={rmCalculationTypes}
            rrpCalculationTypes={rrpCalculationTypes}
            rmRrpDisabled={rmRrpDisabled}
            isCalculationTypeLicensedProducts={isCalcTypeLp}
            isCalculationTypeTargetDrivenLpAndRrp={
              isCalcTypeTargetDrivenLpAndRrp
            }
            validation={visibleValidation}
            onFieldChange={handleFieldChange}
            onFieldBlur={handleFieldBlur}
            onCalculationTypeChange={handleCalculationTypeChange}
          />
          <AdditionalOptionsSection
            calculationBasisValue={calculationBasisValue}
            autoCalcEnabled={autoCalcEnabled}
            calculationBasisOptions={calculationBasisOptions}
            isSimulationSettingsInherited={isSimulationSettingsInherited}
            onFieldChange={handleFieldChange}
          />
        </>
      )}
      <HorizontalLine />
      <SimulationSettingsSection
        simulationSettings={simulationSettings}
        isSimulationSettingsInherited={isSimulationSettingsInherited}
        validation={visibleValidation}
        onFieldChange={handleFieldChange}
        onFieldBlur={handleFieldBlur}
      />

      {/* Footer */}
      <Flex justifyContent="flex-end" gap={3} width="100%" paddingTop={4}>
        {canEdit && (
          <>
            <Button
              appearance="ghost"
              size="medium"
              onClick={handleReset}
              disabled={isCreateMode ? isSaving : !isEdited || isSaving}
            >
              {isCreateMode ? 'Cancel' : 'Reset'}
            </Button>
            <Button
              appearance="solid"
              variant="primary"
              size="medium"
              onClick={() => onSave?.(settingsToSave)}
              loading={isSaving}
              disabled={!isEdited || isSaving || hasValidationErrors}
            >
              {mode === 'create' ? 'Add and save' : 'Save changes'}
            </Button>
          </>
        )}
      </Flex>
    </Flex>
  );
};
